import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';
import ProfileCard from '../ProfileCard'; // Adjust the path if your structure differs
import avatarImage from '../../images/me.jpg';
const StyledAboutSection = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 60px 20px 0 20px;

  .inner {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 60px;
    justify-content: space-between;

    @media (max-width: 900px) {
      flex-direction: column;
      align-items: center;
      gap: 40px;
    }
  }
`;

const StyledText = styled.div`
  flex: 1 1 0%;
  min-width: 260px;
  max-width: 500px;
  margin: 0;

  p {
    margin-bottom: 16px;
    line-height: 1.4;
    color: var(--lightest-slate);
    text-align: left;
  }

  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(120px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-sans);
      font-size: var(--fz-xs);
      font-weight: 500;

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;

const StyledProfileCardWrapper = styled.div`
  flex: 1 1 0%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-width: 280px;
  max-width: 400px;
  margin: 0;
  padding: 0;
  padding-top: 0;

  .simple-photo-wrapper {
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: 900px) {
    margin: 0 auto;
    padding: 0;
    max-width: 100%;
    justify-content: center;
  }
`;

const StyledFullWidthText = styled.div`
  width: 100%;
  margin: 20px 0 0 0;
  padding: 0;

  p {
    margin-bottom: 16px;
    line-height: 1.4;
    color: var(--lightest-slate);
    text-align: left;
  }

  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(120px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-sans);
      font-size: var(--fz-xs);
      font-weight: 500;

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }

  @media (max-width: 900px) {
    margin: 15px 0 0 0;
  }

  @media (max-width: 480px) {
    margin: 12px 0 0 0;
  }
`;
const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return;
    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = ['C++', 'ROS/ROS2', 'JavaScript', 'CSS', 'docker', 'Linux'];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="gradient-text text-xl">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              Hello! My name is Akash, and I work at the intersection of robotics research and real-world deployment. 
              With a background in Aerospace Engineering, my interest evolved into a deep focus on autonomy, 
              exploration algorithms, and how robots perceive and act in complex environments.
            </p>
            <p>
              Today, I develop software for ground and aerial robots operating in GPS-denied, unstructured settings 
              tackling challenges like mission planning, pathfinding, and scalable autonomy pipelines using C++ and ROS2. 
              I enjoy working close to the hardware, building systems that are efficient, reliable, and ready for the field.
            </p>
          </div>
        </StyledText>

        <StyledProfileCardWrapper>
          <ProfileCard
            name="Akash Patel"
            title="Robotics Software Developer"
            handle="aakapatel"
            status="Online"
            contactText="Contact Me"
            avatarUrl={avatarImage}  
            showUserInfo={true}
            enableTilt={true}
            onContactClick={() => window.location.href = 'mailto:aaka.patel@hotmail.com'}
          />
        </StyledProfileCardWrapper>
      </div>

      <div className="inner">
        <StyledFullWidthText>
          <p>
            I'm especially driven by the idea of planetary exploration one day enabling robots to navigate
            Martian lava tubes and subsurface voids, autonomously mapping and planning where no human has
            ever gone. On the side, I also enjoy light web development and designing tools to visualize and
            debug complex robot behavior.
          </p>

          <p>My current development toolkit includes:</p>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledFullWidthText>
      </div>
    </StyledAboutSection>
  );
};

export default About;
